import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useOrganisation } from '~/Hooks';
import { PROVIDER_SERVICE_URLS } from '~/Constants/Urls.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { TIME_VALUES } from '~/Constants/Date.constants';
import { IOrgStaff } from '~/Models/interfaces/OrgStaff.model';
import { IErrorResponse } from '~/Models/DTO/Error.models';

export const useRequestOrgStaff = (staffId?: string) => {
  const { organisation } = useOrganisation();

  return useQuery<IOrgStaff, IErrorResponse>(
    [REQUEST_ACTIONS.GET_ORG_STAFF, organisation?.id, staffId],
    async () =>
      requestReactQuery<IOrgStaff>({
        url: routeReplacer(PROVIDER_SERVICE_URLS.ORGANISATION_STAFF_DETAIL, {
          organisationId: organisation?.id || '0',
          staffId: staffId || '0',
        }),
        config: { method: 'GET' },
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchInterval: TIME_VALUES.HOUR,
      enabled: !!organisation?.id && !!staffId,
    },
  );
};

export const useRequestOrgStaffCreate = () => {
  const { organisation } = useOrganisation();
  const queryClient = useQueryClient();

  return useMutation<IOrgStaff, IErrorResponse, IOrgStaff>(async (staff: IOrgStaff) => {
    const response = await requestReactQuery<IOrgStaff>({
      url: routeReplacer(PROVIDER_SERVICE_URLS.ORGANISATION_STAFF, {
        organisationId: organisation?.id || '0',
      }),
      data: staff,
      config: { method: 'POST' },
    });
    queryClient.invalidateQueries({ queryKey: [REQUEST_ACTIONS.GET_ORG_STUDIO_LIST] });
    queryClient.invalidateQueries({ queryKey: [REQUEST_ACTIONS.GET_ORG_STAFF] });

    return response;
  });
};

export const useRequestOrgStaffUpdate = () => {
  const { organisation } = useOrganisation();
  const queryClient = useQueryClient();

  return useMutation<IOrgStaff, IErrorResponse, IOrgStaff>(async (staff: IOrgStaff) => {
    const response = await requestReactQuery<IOrgStaff>({
      url: routeReplacer(PROVIDER_SERVICE_URLS.ORGANISATION_STAFF, {
        organisationId: organisation?.id || '0',
      }),
      data: staff,
      config: { method: 'PUT' },
    });
    queryClient.invalidateQueries(REQUEST_ACTIONS.GET_ORG_STAFF);
    return response;
  });
};
